import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FormHeader from './components/FormHeader';
import CompanyInformationStep from './components/CompanyInformationStep';
import AddressDetailsStep from './components/AddressDetailsStep';
import BankInformationStep from './components/BankInformationStep';
import CategorizationStep from './components/CategorizationStep';
import ComplianceStep from './components/ComplianceStep';
import AgreementStep from './components/AgreementStep';
import FormNavigation from './components/FormNavigation';
import { validateStep, validateAllStepsForSubmit, canProceedToNextStep, validateField } from './components/FormValidation';
import ErrorSummary from './components/ErrorSummary';
import SessionBanner from './components/SessionBanner';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { registrationService, inviteService, regularizationService, getErrorMessage } from '../../services/apiService';
import { formatPhoneNumber } from '../../utils/phoneCodes';
import { isIndia } from '../../utils/countries';
import { validateMandatoryDocuments } from './utils/documentValidation';
import DocumentVerification from './components/DocumentVerification';
import { useToast } from '../../components/ui/Toast';

// Coerce form values to numbers for API (backend expects int/number, inputs often yield strings)
const toNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const n = Number(typeof value === 'string' ? value.trim() : value);
  return Number.isNaN(n) ? null : n;
};
const toInt = (value) => {
  const n = toNumber(value);
  return n === null ? null : Math.floor(n);
};

// Map frontend MSME status (e.g. 'registered', 'not-registered') to backend enum 'MSME' | 'NON_MSME' | 'PENDING'
const toBackendMsmeStatus = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const v = String(value).toLowerCase();
  const map = {
    'registered': 'MSME', 'msme': 'MSME',
    'not-registered': 'NON_MSME', 'non-msme': 'NON_MSME', 'non_msme': 'NON_MSME',
    'pending': 'PENDING',
  };
  return map[v] || null;
};

const PublicVendorRegistrationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  
  const [totalSteps, setTotalSteps] = useState(6);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [inviteError, setInviteError] = useState('');
  
  // Regularization mode state
  const [regularizationMode, setRegularizationMode] = useState(false);
  const [regularizationToken, setRegularizationToken] = useState(null);
  const [regularizationData, setRegularizationData] = useState(null);
  const [regularizationError, setRegularizationError] = useState(null);
  
  // Staged registration session state
  const [sessionId, setSessionId] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(false);
  const [keepExistingDocuments, setKeepExistingDocuments] = useState({
    bankProof: false,
    panDocument: false,
    gstDocument: false,
    incorporationCertificate: false,
    msmeCertificate: false,
    msmeDeclarationFile: false,
    digitalSignature: false,
  });

  // Initialize form data with default values
  const [formData, setFormData] = useState({
    // Company Information
    businessVertical: 'picl-enterprises',
    companyName: '',
    countryOrigin: '',
    // registrationNumber: removed as per new requirement
    incorporationCertificate: null,
    contactPersonName: '',
    designation: '',
    email: '',
    phoneNumber: '',
    website: '',
    yearEstablished: '',
    businessDescription: '',

    // Address Details
    registeredAddress: '',
    registeredCity: '',
    registeredState: '',
    registeredDistrict: '',
    registeredCountry: '', // Will be auto-populated from countryOrigin
    registeredPincode: '',
    sameAsRegistered: false,
    supplyAddress: '',
    supplyCity: '',
    supplyState: '',
    supplyDistrict: '',
    supplyCountry: '', // Will be auto-populated from countryOrigin
    supplyPincode: '',

    // Bank Information
    bankName: '',
    branchName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    accountType: '',
    ifscCode: '',
    swiftCode: '',
    bankAddress: '',
    bankProof: null,

    // Categorization
    // supplierType, supplierGroup, supplierCategory, and commodityCode removed - collected in approval questionnaire
    annualTurnover: '',
    // productsServices removed
    msmeStatus: '',
    msmeCategory: '',
    msmeNumber: '',
    msmeCertificate: null,
    msmeDeclaration: false,
    msmeDeclarationFile: null,
    // New MSME classification fields
    classificationYear: '',
    classificationDate: '',
    vendorType: '',
    industrySector: '',
    employeeCount: '',
    certifications: '',

    // Compliance
    preferredCurrency: 'INR',
    taxRegistrationNumber: '',
    panNumber: '',
    gstNumber: '',
    panDocument: null,
    gstDocument: null,
    natureOfAssessee: '',
    tanNumber: '',
    placeOfSupply: '',
    vatNumber: '',
    businessLicense: '',
    complianceNotes: '',
    creditRating: '',
    insuranceCoverage: '',
    specialCertifications: '',
    gtaRegistration: '',
    
    // Digital Signature
    digitalSignature: null,

    // Agreements
    agreements: {
      nda: false,
      sqa: false,
      fourM: false,
      codeOfConduct: false,
      complianceAgreement: false,
      selfDeclaration: false,
      overseasAgreement: false
    }
  });

  console.log(formData);
  
  // Convert regularization data to form format
  const convertRegularizationToFormData = (originalData) => {
    return {
      // Company info (from company_info, not company)
      companyName: originalData.company_info?.company_name || '',
      businessVertical: originalData.company_info?.business_vertical || 'picl-enterprises',
      countryOrigin: originalData.addresses?.registered_country || originalData.addresses?.supply_country || '',
      contactPersonName: originalData.company_info?.contact_person_name || '',
      designation: originalData.company_info?.designation || '',
      email: originalData.company_info?.email || '',
      phoneNumber: originalData.company_info?.phone_number || '',
      website: originalData.company_info?.website || '',
      yearEstablished: originalData.company_info?.year_established || '',
      businessDescription: originalData.company_info?.business_description || '',
      
      // Classification (from classification, not company)
      vendorType: originalData.classification?.vendor_type || '',
      industrySector: originalData.classification?.industry_sector || '',
      
      // Financial
      employeeCount: originalData.financial?.employee_count || '',
      annualTurnover: (originalData.financial?.annual_turnover != null && originalData.financial?.annual_turnover !== '')
        ? String(originalData.financial.annual_turnover)
        : '',
      preferredCurrency: originalData.financial?.currency || 'INR',
      
      // Addresses (flat structure, not nested)
      registeredAddress: originalData.addresses?.registered_address || '',
      registeredCity: originalData.addresses?.registered_city || '',
      registeredState: originalData.addresses?.registered_state || '',
      registeredDistrict: originalData.addresses?.registered_district || '',
      registeredCountry: originalData.addresses?.registered_country || '',
      registeredPincode: originalData.addresses?.registered_pincode || '',
      
      supplyAddress: originalData.addresses?.supply_address || '',
      supplyCity: originalData.addresses?.supply_city || '',
      supplyState: originalData.addresses?.supply_state || '',
      supplyDistrict: originalData.addresses?.supply_district || '',
      supplyCountry: originalData.addresses?.supply_country || '',
      supplyPincode: originalData.addresses?.supply_pincode || '',
      
      // Bank (from bank_details, not bank)
      bankName: originalData.bank_details?.bank_name || '',
      branchName: originalData.bank_details?.branch_name || '',
      accountNumber: originalData.bank_details?.account_number || '',
      confirmAccountNumber: originalData.bank_details?.account_number || '',
      accountType: originalData.bank_details?.account_type || '',
      ifscCode: originalData.bank_details?.ifsc_code || '',
      swiftCode: originalData.bank_details?.swift_code || '',
      bankAddress: originalData.bank_details?.bank_address || '',
      
      // MSME
      msmeStatus: originalData.msme?.msme_status || '',
      msmeCategory: originalData.msme?.msme_category || '',
      msmeNumber: originalData.msme?.msme_number || '',
      classificationYear: originalData.msme?.classification_year || '',
      classificationDate: originalData.msme?.classification_date || '',
      
      // Compliance
      panNumber: originalData.compliance?.pan_number || '',
      gstNumber: originalData.compliance?.gst_number || '',
      gtaRegistration: originalData.compliance?.gta_registration || '',
      tanNumber: originalData.compliance?.tan_number || '',
      natureOfAssessee: originalData.compliance?.nature_of_assessee || '',
      placeOfSupply: originalData.compliance?.place_of_supply || '',
      vatNumber: originalData.compliance?.vat_number || '',
      taxRegistrationNumber: originalData.compliance?.tax_registration_number || '',
      businessLicense: originalData.compliance?.business_license || '',
      
      // Agreements
      agreements: originalData.agreements || {
        nda: false,
        sqa: false,
        fourM: false,
        codeOfConduct: false,
        complianceAgreement: false,
        selfDeclaration: false,
        overseasAgreement: false,
      },
    };
  };

  const getFieldNameFromDocType = (docType) => {
    const mapping = {
      'PAN_CARD': 'panDocument',
      'GST_CERTIFICATE': 'gstDocument',
      'BANK_PROOF': 'bankProof',
      'INCORPORATION_CERTIFICATE': 'incorporationCertificate',
      'MSME_CERTIFICATE': 'msmeCertificate',
      'MSME_DECLARATION': 'msmeDeclarationFile',
      'DIGITAL_SIGNATURE': 'digitalSignature',
    };
    return mapping[docType];
  };

  const initializeRegularizationMode = async (token) => {
    try {
      setRegularizationMode(true);
      setRegularizationToken(token);
      setSessionLoading(true);
      setRegularizationError(null);

      // Retrieve data from sessionStorage (set by vendor-regularization-form) or fetch from API if empty
      const storedData = sessionStorage.getItem('regularization_data');
      const storedRequest = sessionStorage.getItem('regularization_request');

      let originalData, request;
      if (storedData && storedRequest) {
        originalData = JSON.parse(storedData);
        request = JSON.parse(storedRequest);
      } else {
        const response = await regularizationService.getRequestByToken(token);
        if (!response?.success || !response?.data?.original_data) {
          throw new Error('Regularization data not found. Please use the link from your email.');
        }
        originalData = response.data.original_data;
        request = response.data.request;
        if (request.status === 'VERIFIED') {
          throw new Error('This regularization link has already been completed.');
        }
        if (request.status === 'EXPIRED') {
          throw new Error('This regularization link has expired. Please contact the administrator.');
        }
        sessionStorage.setItem('regularization_mode', 'true');
        sessionStorage.setItem('regularization_token', token);
        sessionStorage.setItem('regularization_data', JSON.stringify(originalData));
        sessionStorage.setItem('regularization_request', JSON.stringify(request));
      }

      setRegularizationData({ original_data: originalData, request });

      // Convert regularization data to form format
      const prefillData = convertRegularizationToFormData(originalData);
      setFormData(prev => ({ ...prev, ...prefillData }));

      // Handle document references
      if (originalData.documents && originalData.documents.length > 0) {
        const uploadedFileRefs = {};
        originalData.documents.forEach(doc => {
          uploadedFileRefs[doc.type] = {
            file_name: doc.file_name,
            exists: true,
            verified: doc.verified || false,
          };
        });
        setUploadedFiles(uploadedFileRefs);
        
        // Set keep flags for existing documents
        const keepFlags = {};
        originalData.documents.forEach(doc => {
          const fieldName = getFieldNameFromDocType(doc.type);
          if (fieldName) {
            keepFlags[fieldName] = true;
          }
        });
        setKeepExistingDocuments(prev => ({ ...prev, ...keepFlags }));
      }

      // Skip session creation for regularization (atomic submission)
      setSessionId('regularization-' + token); // Dummy session ID for UI compatibility
      setSessionLoading(false);
      
    } catch (err) {
      console.error('Regularization initialization failed:', err);
      setRegularizationError(getErrorMessage(err) || 'Failed to initialize regularization form');
      setSessionLoading(false);
    }
  };
  
  // Handle invite token in URL and start/resume registration session
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    // Check for regularization mode FIRST
    const mode = params.get('mode');
    const regToken = params.get('token');
    
    if (mode === 'regularization' && regToken) {
      console.log('🔄 Regularization mode detected');
      initializeRegularizationMode(regToken);
      return; // Skip normal invite flow
    }
    
    // Otherwise, proceed with normal invite flow
    const token = params.get('invite') || '';
    
    // Validate token format (should be non-empty string)
    if (!token || token.trim().length === 0) {
      console.error('❌ No invite token found in URL');
      setInviteError('Invite token is required. Please use the complete invite link provided in your email.');
      setSessionLoading(false);
      return;
    }
    
    // Basic token format validation (should be a reasonable length)
    if (token.length < 10) {
      console.error('❌ Invalid invite token format (too short):', token.length);
      setInviteError('Invalid invite link format. Please use the complete invite link provided in your email.');
      setSessionLoading(false);
      return;
    }
    
    console.log('✅ Invite token extracted from URL');
    setInviteToken(token);
    
    const initializeSession = async () => {
      setSessionLoading(true);
      setInviteError(''); // Clear any previous errors
      
      try {
        console.log('🔍 Starting session initialization with token:', token.substring(0, 8) + '...');
        
        // First validate the invite token
        console.log('📧 Validating invite token...');
        let inviteResponse;
        try {
          inviteResponse = await inviteService.validateToken(token);
          console.log('📧 Invite validation response:', inviteResponse);
        } catch (inviteError) {
          console.error('❌ Invite validation failed:', inviteError);
          const errorDetails = {
            message: inviteError?.message || 'Unknown error',
            response: inviteError?.response?.data,
            status: inviteError?.response?.status,
            url: inviteError?.config?.url,
          };
          console.error('❌ Invite validation error details:', errorDetails);
          
          if (inviteError?.response?.status === 404) {
            throw new Error('Invalid invite link. The invite token was not found.');
          } else if (inviteError?.response?.status === 400) {
            const backendMsg = inviteError?.response?.data?.error?.message || inviteError?.response?.data?.message;
            throw new Error(backendMsg || 'Invite link is invalid or expired.');
          } else if (inviteError?.response?.status === 401) {
            throw new Error('Invite link has expired. Please request a new invite.');
          } else if (!inviteError?.response) {
            throw new Error('Network error. Please check your internet connection and try again.');
          } else {
            const backendMsg = getErrorMessage(inviteError);
            throw new Error(backendMsg || 'Failed to validate invite link. Please check your invite link and try again.');
          }
        }
        
        if (!inviteResponse || !inviteResponse.success) {
          throw new Error(inviteResponse?.message || 'Invalid invite token response');
        }
        
        if (!inviteResponse.data?.email) {
          throw new Error('Invite token is missing required information');
        }
        
        setInviteEmail(inviteResponse.data.email);
        setFormData((prev) => ({ 
          ...prev, 
          email: inviteResponse.data.email,
          contactPersonName: inviteResponse.data.name || prev.contactPersonName,
        }));
        console.log('✅ Invite token validated successfully');
        
        // Check for existing session in localStorage
        // Only resume if the stored session is for the SAME invite token
        const storedSession = localStorage.getItem('PICL_registration_session');
        if (storedSession) {
          try {
            const stored = JSON.parse(storedSession);
            const session_id = stored.session_id || stored.id;
            const expires_at = stored.expires_at;
            const storedInviteToken = stored.invite_token;
            
            // Only resume if it's the same invite token and session is still valid
            if (session_id && expires_at && storedInviteToken === token && new Date(expires_at) > new Date()) {
              console.log('🔄 Attempting to resume existing session for same invite token:', session_id);
              // Try to resume existing session
              const resumeResponse = await registrationService.resumeSession(session_id);
              console.log('🔄 Resume session response:', resumeResponse);
              
              if (resumeResponse.success && resumeResponse.data) {
                setIsRestoringSession(true); // Prevent hasUnsavedChanges during restoration
                const session = resumeResponse.data;
                // Handle both session_id and id properties for compatibility
                const actualSessionId = session.session_id || session.id;
                
                if (!actualSessionId) {
                  throw new Error('Session response missing session ID');
                }
                
                setSessionId(actualSessionId);
                setSessionExpiresAt(session.expires_at);
                
                // Debug logging for date parsing verification
                console.log('🔄 Session resumed - expiration date:', {
                  raw: session.expires_at,
                  parsed: new Date(session.expires_at),
                  now: new Date(),
                  timeUntilExpiry: session.expires_at ? new Date(session.expires_at).getTime() - new Date().getTime() : null,
                  daysRemaining: session.expires_at ? Math.floor((new Date(session.expires_at).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null,
                });
                
                // Restore form data from session (backend returns registration_data, not form_data)
                const registrationData = session.registration_data || session.form_data;
                if (registrationData) {
                  try {
                    const restoredData = typeof registrationData === 'string' 
                      ? JSON.parse(registrationData) 
                      : registrationData;
                    setFormData(prev => ({ ...prev, ...flattenSessionData(restoredData) }));
                    console.log('✅ Form data restored from session');
                  } catch (parseError) {
                    console.warn('⚠️ Failed to parse restored form data:', parseError);
                  }
                }
                
                // Restore uploaded files info
                if (session.staged_files?.length > 0) {
                  const files = {};
                  const formDataUpdates = {};
                  
                  // File type to formData field mapping
                  const fieldMapping = {
                    'PAN_CARD': 'panDocument',
                    'GST_CERTIFICATE': 'gstDocument',
                    'BANK_PROOF': 'bankProof',
                    'DIGITAL_SIGNATURE': 'digitalSignature',
                    'MSME_CERTIFICATE': 'msmeCertificate',
                    'MSME_DECLARATION': 'msmeDeclarationFile',
                    'INCORPORATION_CERTIFICATE': 'incorporationCertificate'
                  };
                  
                  session.staged_files.forEach(f => {
                    const fileInfo = { 
                      id: f.id, 
                      file_name: f.file_name,
                      file_size: f.file_size,
                      mime_type: f.mime_type
                    };
                    
                    files[f.file_type] = fileInfo;
                    
                    // Also sync to formData field
                    const fieldName = fieldMapping[f.file_type];
                    if (fieldName) {
                      formDataUpdates[fieldName] = fileInfo;
                    }
                  });
                  
                  setUploadedFiles(files);
                  setFormData(prev => ({ ...prev, ...formDataUpdates }));
                  console.log('✅ Uploaded files info restored and synced to formData');
                }
                
                console.log('✅ Session resumed successfully:', actualSessionId);
                setSessionLoading(false);
                setIsRestoringSession(false); // Re-enable change tracking after restoration
                return;
              } else {
                console.warn('⚠️ Resume session response was not successful, starting new session');
              }
            } else {
              if (storedInviteToken && storedInviteToken !== token) {
                console.log('ℹ️ Stored session is for different invite token, clearing old session');
                localStorage.removeItem('PICL_registration_session');
              } else {
                console.log('ℹ️ Stored session expired or invalid, starting new session');
              }
            }
          } catch (e) {
            console.warn('⚠️ Failed to resume session, starting new one:', e);
            localStorage.removeItem('PICL_registration_session');
          }
        }
        
        // Start new registration session
        console.log('🆕 Starting new registration session...');
        const sessionResponse = await registrationService.startSession(token, getClientFingerprint());
        console.log('🆕 Start session response:', sessionResponse);
        
        if (!sessionResponse || !sessionResponse.success) {
          throw new Error(sessionResponse?.message || sessionResponse?.error?.message || 'Failed to start registration session');
        }
        
        if (!sessionResponse.data) {
          throw new Error('Session response missing data');
        }
        
        // Handle both session_id and id properties for compatibility
        const sessionId = sessionResponse.data.session_id || sessionResponse.data.id;
        const expiresAt = sessionResponse.data.expires_at;
        
        if (!sessionId) {
          throw new Error('Session response missing session ID');
        }
        
        if (!expiresAt) {
          throw new Error('Session response missing expiration date');
        }
        
        setSessionId(sessionId);
        setSessionExpiresAt(expiresAt);
        
        // Debug logging for date parsing verification
        console.log('🆕 New session created - expiration date:', {
          raw: expiresAt,
          parsed: new Date(expiresAt),
          now: new Date(),
          timeUntilExpiry: expiresAt ? new Date(expiresAt).getTime() - new Date().getTime() : null,
          daysRemaining: expiresAt ? Math.floor((new Date(expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null,
        });
        
        setIsRestoringSession(true); // Prevent hasUnsavedChanges during initial session setup
        // Pre-fill form data if provided (for rectification invites)
        if (sessionResponse.data.prefill_data) {
          try {
            const prefillData = sessionResponse.data.prefill_data;
            const flattenedData = flattenSessionData(prefillData);
            setFormData(prev => ({ ...prev, ...flattenedData }));
            console.log('✅ Form data pre-filled from rectification invite:', flattenedData);
          } catch (prefillError) {
            console.warn('⚠️ Failed to pre-fill form data:', prefillError);
          }
        }
        
        // Pre-fill uploaded files if provided (for rectification invites)
        if (sessionResponse.data.staged_files && sessionResponse.data.staged_files.length > 0) {
          try {
            const files = {};
            const formDataUpdates = {};
            
            // File type to formData field mapping
            const fieldMapping = {
              'PAN_CARD': 'panDocument',
              'GST_CERTIFICATE': 'gstDocument',
              'BANK_PROOF': 'bankProof',
              'DIGITAL_SIGNATURE': 'digitalSignature',
              'MSME_CERTIFICATE': 'msmeCertificate',
              'MSME_DECLARATION': 'msmeDeclarationFile',
              'INCORPORATION_CERTIFICATE': 'incorporationCertificate'
            };
            
            const keepExisting = {};
            
            sessionResponse.data.staged_files.forEach(f => {
              const fileInfo = { 
                id: f.id, 
                file_name: f.file_name,
                file_size: f.file_size,
                mime_type: f.mime_type
              };
              
              files[f.file_type] = fileInfo;
              
              // Also sync to formData field
              const fieldName = fieldMapping[f.file_type];
              if (fieldName) {
                formDataUpdates[fieldName] = fileInfo;
                // Mark as "keep existing" by default for pre-filled documents
                keepExisting[fieldName] = true;
              }
            });
            
            setUploadedFiles(files);
            setFormData(prev => ({ ...prev, ...formDataUpdates }));
            setKeepExistingDocuments(prev => ({ ...prev, ...keepExisting }));
            console.log('✅ Uploaded files pre-filled from rectification invite and synced to formData:', files);
          } catch (filesError) {
            console.warn('⚠️ Failed to pre-fill uploaded files:', filesError);
          }
        }
        
        // Store session in localStorage for resumability
        localStorage.setItem('PICL_registration_session', JSON.stringify({
          session_id: sessionId,
          expires_at: expiresAt,
          invite_token: token,
        }));
        
        setSessionLoading(false);
        setIsRestoringSession(false); // Re-enable change tracking after initial setup
        console.log('✅ New session started successfully:', sessionId);
      } catch (e) {
        console.error('❌ Session initialization failed:', e);
        console.error('❌ Error details:', {
          message: e?.message || 'Unknown error',
          response: e?.response?.data,
          status: e?.response?.status,
          url: e?.config?.url,
          stack: e?.stack,
        });
        
        // Extract more specific error message
        let errorMsg = getErrorMessage(e);
        
        // If we already have a specific error message from our validation, use it
        if (e?.message && e.message !== errorMsg) {
          errorMsg = e.message;
        }
        
        // Provide fallback if error message is still generic
        if (!errorMsg || errorMsg === 'An unexpected error occurred') {
          if (e?.response?.status === 404) {
            errorMsg = 'Invalid invite link. The invite token was not found.';
          } else if (e?.response?.status === 400) {
            errorMsg = 'Invalid request. Please check your invite link and try again.';
          } else if (e?.response?.status === 401) {
            errorMsg = 'Invite link has expired. Please request a new invite.';
          } else if (e?.response?.status === 500) {
            errorMsg = 'Server error. Please try again later or contact support.';
          } else if (!e?.response) {
            errorMsg = 'Network error. Please check your internet connection and try again.';
          } else {
            errorMsg = 'Unable to start registration session. Please check your invite link and try again.';
          }
        }
        
        setInviteError(errorMsg);
      } finally {
        setSessionLoading(false);
      }
    };
    
    initializeSession();
  }, [location.search]);
  
  // Helper to generate client fingerprint
  const getClientFingerprint = () => {
    const nav = window.navigator;
    const screen = window.screen;
    const fingerprint = [
      nav.userAgent,
      nav.language,
      screen.colorDepth,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
    ].join('|');
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  };
  
  // Helper to flatten session data and convert snake_case to camelCase
  const flattenSessionData = (sessionData) => {
    const flat = {};
    const agreements = {};
    
    // Helper to convert snake_case to camelCase
    const toCamelCase = (str) => {
      return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    };
    
    // Agreement field names that should be nested
    const agreementFields = ['nda', 'sqa', 'fourM', 'codeOfConduct', 'complianceAgreement', 'selfDeclaration', 'overseasAgreement'];
    
    Object.entries(sessionData).forEach(([stepKey, stepData]) => {
      if (stepData && typeof stepData === 'object') {
        Object.entries(stepData).forEach(([key, value]) => {
          // Convert snake_case to camelCase
          const camelKey = toCamelCase(key);
          
          // Check if this is an agreement field (from step6)
          if (stepKey === 'step6' && agreementFields.includes(camelKey)) {
            agreements[camelKey] = value;
          } else {
            flat[camelKey] = value;
          }
        });
      }
    });
    
    // Add agreements as nested object if any were found
    if (Object.keys(agreements).length > 0) {
      flat.agreements = agreements;
    }
    
    return flat;
  };
  
  // Calculate time remaining for session
  const getTimeRemaining = useCallback(() => {
    if (!sessionExpiresAt) return null;
    const now = new Date();
    const expiry = new Date(sessionExpiresAt);
    const diff = expiry - now;
    if (diff <= 0) return { expired: true };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return { expired: false, days, hours, minutes, isExpiringSoon: days < 2 };
  }, [sessionExpiresAt]);

  // Update time remaining in real-time
  useEffect(() => {
    // Calculate initial time remaining
    const updateTimeRemaining = () => {
      const remaining = getTimeRemaining();
      setTimeRemaining(remaining);
      
      // Debug logging to verify date parsing
      if (sessionExpiresAt && remaining) {
        console.log('⏰ Time remaining update:', {
          expiresAt: sessionExpiresAt,
          parsedExpiry: new Date(sessionExpiresAt),
          now: new Date(),
          remaining: `${remaining.days}d ${remaining.hours}h ${remaining.minutes}m`,
        });
      }
    };

    updateTimeRemaining();

    // Set up interval to update every 30 seconds
    const intervalId = setInterval(updateTimeRemaining, 30000);

    // Cleanup interval on unmount or when sessionExpiresAt changes
    return () => clearInterval(intervalId);
  }, [sessionExpiresAt, getTimeRemaining]);

  // Load saved form data from localStorage on component mount (text fields only, as fallback)
  // Note: Files are ALWAYS restored from server sessions, never from localStorage
  useEffect(() => {
    // Only restore from localStorage if we don't have a server session yet
    // Server session restoration will happen in initializeSession
    if (!sessionId && !sessionLoading) {
      const savedData = localStorage.getItem('vendorRegistrationForm');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          
          // Remove any file fields that might have been incorrectly stored
          const fileFields = ['panDocument', 'gstDocument', 'bankProof', 'digitalSignature', 
                             'msmeCertificate', 'msmeDeclarationFile', 'incorporationCertificate'];
          fileFields.forEach(field => {
            delete parsedData[field];
          });
          
          // Clear invalid supplier types that don't match backend enum
          // supplierType removed from validation
          
          setFormData(parsedData);
          console.log('📝 Restored text fields from localStorage (fallback - no server session yet)');
        } catch (error) {
          console.error('Error loading saved form data:', error);
        }
      }
    }
  }, [sessionId, sessionLoading]);

  // Save form data to localStorage whenever it changes (for text fields only)
  useEffect(() => {
    // Only save text fields to localStorage, exclude file objects
    const textFieldsOnly = { ...formData };
    // Remove file objects that can't be serialized
    const fileFields = ['panDocument', 'gstDocument', 'bankProof', 'digitalSignature', 
                       'msmeCertificate', 'msmeDeclarationFile', 'incorporationCertificate'];
    fileFields.forEach(field => {
      if (textFieldsOnly[field] instanceof File) {
        // Don't save File objects to localStorage
        delete textFieldsOnly[field];
      }
    });
    localStorage.setItem('vendorRegistrationForm', JSON.stringify(textFieldsOnly));
    // Mark as having unsaved changes (will trigger auto-save)
    // But skip during session restoration to prevent unnecessary saves
    if (sessionId && !regularizationMode && !isRestoringSession) {
      setHasUnsavedChanges(true);
    }
  }, [formData, sessionId, regularizationMode]);
  
  // Save step data to server (staged registration)
  const saveStepData = useCallback(async (step, data) => {
    if (!sessionId) {
      console.warn('No session ID, cannot save step data');
      return false;
    }
    try {
      await registrationService.saveStepData(sessionId, step, data);
      console.log(`✅ Step ${step} data saved to server`);
      return true;
    } catch (error) {
      console.error(`Failed to save step ${step} data:`, error);
      return false;
    }
  }, [sessionId]);

  // Get step data for saving
  const getStepData = useCallback((step) => {
    // For foreign vendors, step 5 is Agreements (they skip Compliance step)
    // We need to return agreements data when on step 5 for foreign vendors
    const isForeignVendor = !isIndia(formData.countryOrigin);
    const effectiveStep = (isForeignVendor && step === 5) ? 6 : step;
    
    switch (effectiveStep) {
      case 1:
        return {
          businessVertical: formData.businessVertical,
          companyName: formData.companyName,
          countryOrigin: formData.countryOrigin,
          contactPersonName: formData.contactPersonName,
          designation: formData.designation,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          website: formData.website,
          yearEstablished: toInt(formData.yearEstablished) ?? formData.yearEstablished,
          businessDescription: formData.businessDescription,
        };
      case 2:
        return {
          registeredAddress: formData.registeredAddress,
          registeredCity: formData.registeredCity,
          registeredState: formData.registeredState,
          registeredDistrict: formData.registeredDistrict,
          registeredCountry: formData.registeredCountry,
          registeredPincode: formData.registeredPincode,
          supplyAddress: formData.supplyAddress,
          supplyCity: formData.supplyCity,
          supplyState: formData.supplyState,
          supplyDistrict: formData.supplyDistrict,
          supplyCountry: formData.supplyCountry,
          supplyPincode: formData.supplyPincode,
        };
      case 3:
        return {
          bankName: formData.bankName,
          branchName: formData.branchName,
          accountNumber: formData.accountNumber,
          accountType: formData.accountType,
          ifscCode: formData.ifscCode,
          swiftCode: formData.swiftCode,
          bankAddress: formData.bankAddress,
        };
      case 4:
        return {
          annualTurnover: toNumber(formData.annualTurnover) ?? formData.annualTurnover,
          msmeStatus: toBackendMsmeStatus(formData.msmeStatus) ?? formData.msmeStatus,
          msmeCategory: formData.msmeCategory,
          msmeNumber: formData.msmeNumber,
          classificationYear: toInt(formData.classificationYear) ?? formData.classificationYear,
          classificationDate: formData.classificationDate,
          vendorType: formData.vendorType,
          industrySector: formData.industrySector,
          employeeCount: toInt(formData.employeeCount) ?? formData.employeeCount,
          preferredCurrency: formData.preferredCurrency,
        };
      case 5:
        return {
          panNumber: formData.panNumber,
          gstNumber: formData.gstNumber,
          taxRegistrationNumber: formData.taxRegistrationNumber,
          vatNumber: formData.vatNumber,
          businessLicense: formData.businessLicense,
          gtaRegistration: formData.gtaRegistration,
          natureOfAssessee: formData.natureOfAssessee,
        };
      case 6:
        return {
          nda: formData.agreements?.nda || false,
          sqa: formData.agreements?.sqa || false,
          fourM: formData.agreements?.fourM || false,
          codeOfConduct: formData.agreements?.codeOfConduct || false,
          complianceAgreement: formData.agreements?.complianceAgreement || false,
          selfDeclaration: formData.agreements?.selfDeclaration || false,
          overseasAgreement: formData.agreements?.overseasAgreement || false, // Added for foreign vendors
        };
      default:
        return {};
    }
  }, [formData]);

  // Auto-save current step data to server every 30 seconds
  useEffect(() => {
    if (!sessionId || regularizationMode || !hasUnsavedChanges) {
      return;
    }

    const autoSaveInterval = setInterval(async () => {
      if (hasUnsavedChanges && !isAutoSaving) {
        setIsAutoSaving(true);
        try {
          const stepData = getStepData(currentStep);
          const isForeignVendor = !isIndia(formData.countryOrigin);
          const effectiveStepNumber = (isForeignVendor && currentStep === 5) ? 6 : currentStep;
          const success = await saveStepData(effectiveStepNumber, stepData);
          if (success) {
            setLastAutoSave(new Date());
            setHasUnsavedChanges(false);
            console.log('✅ Auto-saved draft at', new Date().toLocaleTimeString());
          }
        } catch (error) {
          console.error('Auto-save failed:', error);
        } finally {
          setIsAutoSaving(false);
        }
      }
    }, 30000); // 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [sessionId, currentStep, hasUnsavedChanges, isAutoSaving, regularizationMode, saveStepData, getStepData, formData]);
  
  // Upload file to server (staged registration)
  const uploadFile = useCallback(async (file, fileType) => {
    if (!sessionId) {
      console.warn('No session ID, cannot upload file');
      return null;
    }
    try {
      setUploadProgress(prev => ({ ...prev, [fileType]: 0 }));
      
      const response = await registrationService.uploadFile(
        sessionId,
        file,
        fileType,
        (progress) => setUploadProgress(prev => ({ ...prev, [fileType]: progress }))
      );
      
      if (response.success && response.data) {
        setUploadedFiles(prev => ({
          ...prev,
          [fileType]: { id: response.data.id, file_name: file.name }
        }));
        setUploadProgress(prev => ({ ...prev, [fileType]: 100 }));
        console.log(`✅ File ${fileType} uploaded:`, response.data.id);
        return response.data;
      }
      return null;
    } catch (error) {
      console.error(`Failed to upload ${fileType}:`, error);
      setUploadProgress(prev => ({ ...prev, [fileType]: -1 }));
      throw error;
    }
  }, [sessionId]);
  
  // Handle document preview
  const handleDocumentPreview = useCallback(async (file, fileType) => {
    try {
      let blob;
      const isRegularization = sessionId && String(sessionId).startsWith('regularization-');
      const regToken = isRegularization ? String(sessionId).replace(/^regularization-/, '') : null;

      if (file instanceof File) {
        blob = file;
      } else if (file?.id && sessionId && !isRegularization) {
        blob = await registrationService.previewStagedFile(sessionId, file.id);
      } else if (regToken && fileType) {
        // Existing vendor document (e.g. "Keep existing" in regularization flow)
        blob = await regularizationService.previewDocument(regToken, fileType);
      } else {
        console.error('Cannot preview file - no File object, file ID, or regularization token');
        toast.error('Preview is not available for this document.');
        return;
      }

      const url = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Failed to preview document:', error);
      toast.error('Failed to preview document. Please try again.');
    }
  }, [sessionId]);
  
  const updateFormData = (updates) => {
    setFormData(prev => {
      const newFormData = { ...prev, ...updates };
      
      // Auto-sync country selection from Company Information to Address Details
      if (updates.countryOrigin && updates.countryOrigin !== prev.countryOrigin) {
        // Update both registered and supply country to match the selected country origin
        newFormData.registeredCountry = updates.countryOrigin;
        newFormData.supplyCountry = updates.countryOrigin;
        
        // Clear state fields when country changes (as states are country-specific)
        newFormData.registeredState = '';
        newFormData.supplyState = '';
        
        // Clear supplier category and MSME fields for non-Indian countries
        if (!isIndia(updates.countryOrigin)) {
          newFormData.supplierCategory = '';
          newFormData.msmeStatus = '';
          newFormData.msmeCategory = '';
          newFormData.msmeNumber = '';
          newFormData.msmeCertificate = null;
          newFormData.msmeDeclaration = false;
        }

        // Set default currency to INR for Indian vendors
        if (isIndia(updates.countryOrigin)) {
          newFormData.preferredCurrency = 'INR';
        }
        
        // Adjust total step count: foreign vendors skip Compliance step (Step 5)
        setTotalSteps(isIndia(updates.countryOrigin) ? 6 : 5);
      }
      
      // Auto-format phone number when country origin changes
      if (updates.countryOrigin && updates.countryOrigin !== prev.countryOrigin) {
        newFormData.phoneNumber = formatPhoneNumber(updates.countryOrigin, newFormData.phoneNumber);
      }
      
      return newFormData;
    });
    
    // Validate updated fields immediately and set/clear error messages
    const updatedFields = Object.keys(updates);
    setErrors(prev => {
      const newErrors = { ...prev };
      updatedFields.forEach(field => {
        const err = validateField(field, updates[field], {
          ...formData,
          ...updates,
        });
        if (err) newErrors[field] = err; else delete newErrors[field];
      });

      // If country changed, re-validate dependent fields
      if (updatedFields.includes('countryOrigin')) {
        const deps = [
          'phoneNumber',
          'registeredPincode',
          'supplyPincode',
          'ifscCode',
          'gstNumber',
          'panNumber',
          'msmeStatus',
          'msmeCategory',
          'msmeNumber',
          'swiftCode',
        ];
        deps.forEach(dep => {
          const val = (formData[dep] !== undefined ? formData[dep] : '') || '';
          const err = validateField(dep, val, {
            ...formData,
            ...updates,
            [dep]: val,
          });
          if (err) newErrors[dep] = err; else delete newErrors[dep];
        });
      }

      return newErrors;
    });
  };


  const getEffectiveValidationStep = (step) => {
    // When foreign vendors are on step 5 (Agreements), reuse Agreements validation (step 6)
    if (!isIndia(formData.countryOrigin) && step === 5) return 6;
    return step;
  };

  const handleNext = async () => {
    const stepErrors = validateStep(getEffectiveValidationStep(currentStep), formData, keepExistingDocuments);
    setErrors(stepErrors);

    if (Object.keys(stepErrors).length === 0) {
      // Save current step data to server before moving to next step (skip for regularization mode)
      if (sessionId && !regularizationMode) {
        const stepData = getStepData(currentStep);
        // For foreign vendors, save step 5 (agreements) as step 6 for backend compatibility
        const isForeignVendor = !isIndia(formData.countryOrigin);
        const effectiveStepNumber = (isForeignVendor && currentStep === 5) ? 6 : currentStep;
        await saveStepData(effectiveStepNumber, stepData);
        setHasUnsavedChanges(false); // Clear unsaved changes flag after manual save
      }
      
      // If moving from step 4 to 5 and vendor is foreign, go to Agreements which is step 5
      const next = currentStep + 1;
      if (next === 5 && !isIndia(formData.countryOrigin)) {
        setCurrentStep(5);
      } else {
        setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      }
      setShowValidationError(false);
      window.scrollTo(0, 0);
    } else {
      // Show validation error notification
      setShowValidationError(true);
      // Scroll to top to show error summary
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Hide notification after 6 seconds
      setTimeout(() => setShowValidationError(false), 6000);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleGeneratePDF = () => {
    // Mock PDF generation
    const pdfContent = `
      VENDOR REGISTRATION AGREEMENT
      
      Company: ${formData.companyName}
      Registration Number: ${''}
      Contact Person: ${formData.contactPersonName}
      Email: ${formData.email}
      
      AGREEMENTS ACCEPTED:
      ${Object.entries(formData.agreements)
        .filter(([_, accepted]) => accepted)
        .map(([agreement, _]) => `✓ ${agreement.toUpperCase()}`)
        .join('\n')}
      
      Date: ${new Date().toLocaleDateString()}
      Time: ${new Date().toLocaleTimeString()}
      
      This document serves as confirmation of digital agreement acceptance.
    `;

    // Create and download PDF (mock implementation)
    const blob = new Blob([pdfContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendor-agreement-${formData.companyName.replace(/\s+/g, '-').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const extractRegularizationUpdates = (current, original) => {
    const updates = {};
    
    // Company info (from company_info, not company)
    if (current.companyName !== original.company_info?.company_name) updates.company_name = current.companyName;
    if (current.businessVertical !== original.company_info?.business_vertical) updates.business_vertical = current.businessVertical;
    // country_origin is derived from addresses, compare with registered_country
    const originalCountryOrigin = original.addresses?.registered_country || original.addresses?.supply_country;
    if (current.countryOrigin !== originalCountryOrigin) updates.country_origin = current.countryOrigin;
    if (current.contactPersonName !== original.company_info?.contact_person_name) updates.contact_person_name = current.contactPersonName;
    if (current.designation !== original.company_info?.designation) updates.designation = current.designation;
    if (current.email !== original.company_info?.email) updates.email = current.email;
    if (current.phoneNumber !== original.company_info?.phone_number) updates.phone_number = current.phoneNumber;
    if (current.website !== original.company_info?.website) updates.website = current.website;
    if (current.yearEstablished !== original.company_info?.year_established) {
      const val = toInt(current.yearEstablished);
      if (val !== null) updates.year_established = val;
    }
    if (current.businessDescription !== original.company_info?.business_description) updates.business_description = current.businessDescription;
    
    // Classification (from classification, not company)
    if (current.vendorType !== original.classification?.vendor_type) updates.vendor_type = current.vendorType;
    if (current.industrySector !== original.classification?.industry_sector) updates.industry_sector = current.industrySector;
    
    // Financial (coerce to number for API)
    if (current.employeeCount !== original.financial?.employee_count) {
      const val = toInt(current.employeeCount);
      if (val !== null) updates.employee_count = val;
    }
    
    // Addresses (flat structure, not nested)
    if (current.registeredAddress !== original.addresses?.registered_address) updates.registered_address = current.registeredAddress;
    if (current.registeredCity !== original.addresses?.registered_city) updates.registered_city = current.registeredCity;
    if (current.registeredState !== original.addresses?.registered_state) updates.registered_state = current.registeredState;
    if (current.registeredDistrict !== original.addresses?.registered_district) updates.registered_district = current.registeredDistrict;
    if (current.registeredCountry !== original.addresses?.registered_country) updates.registered_country = current.registeredCountry;
    if (current.registeredPincode !== original.addresses?.registered_pincode) updates.registered_pincode = current.registeredPincode;
    
    if (current.supplyAddress !== original.addresses?.supply_address) updates.supply_address = current.supplyAddress;
    if (current.supplyCity !== original.addresses?.supply_city) updates.supply_city = current.supplyCity;
    if (current.supplyState !== original.addresses?.supply_state) updates.supply_state = current.supplyState;
    if (current.supplyDistrict !== original.addresses?.supply_district) updates.supply_district = current.supplyDistrict;
    if (current.supplyCountry !== original.addresses?.supply_country) updates.supply_country = current.supplyCountry;
    if (current.supplyPincode !== original.addresses?.supply_pincode) updates.supply_pincode = current.supplyPincode;
    
    // Bank (from bank_details, not bank)
    if (current.bankName !== original.bank_details?.bank_name) updates.bank_name = current.bankName;
    if (current.branchName !== original.bank_details?.branch_name) updates.branch_name = current.branchName;
    if (current.accountType !== original.bank_details?.account_type) updates.account_type = current.accountType;
    if (current.ifscCode !== original.bank_details?.ifsc_code) updates.ifsc_code = current.ifscCode;
    if (current.swiftCode !== original.bank_details?.swift_code) updates.swift_code = current.swiftCode;
    if (current.bankAddress !== original.bank_details?.bank_address) updates.bank_address = current.bankAddress;
    // Only include account_number if it was changed (not masked)
    if (current.accountNumber && current.accountNumber !== '***' && current.accountNumber !== original.bank_details?.account_number) {
      updates.account_number = current.accountNumber;
    }
    
    // MSME (map frontend 'registered'/'not-registered' to backend enum 'MSME' | 'NON_MSME' | 'PENDING')
    if (current.msmeStatus !== original.msme?.msme_status) {
      const mapped = toBackendMsmeStatus(current.msmeStatus);
      if (mapped) updates.msme_status = mapped;
    }
    if (current.msmeCategory !== original.msme?.msme_category) updates.msme_category = current.msmeCategory;
    if (current.msmeNumber !== original.msme?.msme_number) updates.msme_number = current.msmeNumber;
    if (current.classificationYear !== original.msme?.classification_year) updates.classification_year = current.classificationYear;
    if (current.classificationDate !== original.msme?.classification_date) updates.classification_date = current.classificationDate;
    
    // Compliance
    if (current.panNumber !== original.compliance?.pan_number) updates.pan_number = current.panNumber;
    if (current.gstNumber !== original.compliance?.gst_number) updates.gst_number = current.gstNumber;
    if (current.gtaRegistration !== original.compliance?.gta_registration) updates.gta_registration = current.gtaRegistration;
    if (current.tanNumber !== original.compliance?.tan_number) updates.tan_number = current.tanNumber;
    if (current.natureOfAssessee !== original.compliance?.nature_of_assessee) updates.nature_of_assessee = current.natureOfAssessee;
    if (current.placeOfSupply !== original.compliance?.place_of_supply) updates.place_of_supply = current.placeOfSupply;
    if (current.vatNumber !== original.compliance?.vat_number) updates.vat_number = current.vatNumber;
    if (current.taxRegistrationNumber !== original.compliance?.tax_registration_number) updates.tax_registration_number = current.taxRegistrationNumber;
    if (current.businessLicense !== original.compliance?.business_license) updates.business_license = current.businessLicense;
    
    // Financial (coerce to number for API)
    if (current.annualTurnover !== original.financial?.annual_turnover) {
      const val = toNumber(current.annualTurnover);
      if (val !== null) updates.annual_turnover = val;
    }
    if (current.preferredCurrency !== original.financial?.currency) updates.currency = current.preferredCurrency;
    
    // Agreements
    const agreementsChanged = JSON.stringify(current.agreements) !== JSON.stringify(original.agreements);
    if (agreementsChanged) {
      updates.agreements = current.agreements;
    }
    
    return updates;
  };

  const handleSubmit = async () => {
    const stepErrors = validateAllStepsForSubmit(formData, keepExistingDocuments);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) {
      return;
    }

    // Validate mandatory documents
    const docValidation = validateMandatoryDocuments(formData, keepExistingDocuments, uploadedFiles);
    if (!docValidation.isValid) {
      const errorMessage = docValidation.errors.join('\n');
      setErrors({ submit: `Missing or invalid documents:\n${errorMessage}` });
      return;
    }

    // REGULARIZATION MODE SUBMISSION
    if (regularizationMode) {
      if (!regularizationToken || !regularizationData) {
        setErrors({ submit: 'Regularization data not found. Please refresh and try again.' });
        return;
      }
      
      setIsSubmitting(true);
      
      try {
        // Extract only changed fields
        const updates = extractRegularizationUpdates(formData, regularizationData.original_data);
        
        // Note: Document uploads in regularization mode will be handled in a future update
        // For now, we only send text field updates. Documents can be updated separately if needed.
        // The existing document references are preserved in original_data.
        
        // Submit to regularization endpoint
        const response = await regularizationService.submitUpdates(regularizationToken, updates);
        
        if (response.success) {
          setIsSubmitted(true);
          // Clear sessionStorage
          sessionStorage.removeItem('regularization_mode');
          sessionStorage.removeItem('regularization_token');
          sessionStorage.removeItem('regularization_data');
          sessionStorage.removeItem('regularization_request');
        }
      } catch (err) {
        const errorMsg = getErrorMessage(err) || 'Failed to submit updates';
        setErrors({ submit: errorMsg });
        console.error('Regularization submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // NORMAL REGISTRATION SUBMISSION (existing logic)
    {
      if (!sessionId) {
        setErrors({ submit: 'No active registration session. Please refresh and try again.' });
        return;
      }
      
      setIsSubmitting(true);
      
      try {
        console.log('📤 Starting staged registration submission...');
        
        // Step 1: Save final step data (agreements)
        const finalStepData = getStepData(currentStep);
        // For foreign vendors, save step 5 (agreements) as step 6 for backend compatibility
        const isForeignVendor = !isIndia(formData.countryOrigin);
        const effectiveStepNumber = (isForeignVendor && currentStep === 5) ? 6 : currentStep;
        await saveStepData(effectiveStepNumber, finalStepData);
        console.log('✅ Final step data saved');
        
        // Step 2: Upload any files that haven't been uploaded yet
        const filesToUpload = [];
        
        // Digital signature (required for all)
        if (formData.digitalSignature && !uploadedFiles['DIGITAL_SIGNATURE']) {
          filesToUpload.push({ file: formData.digitalSignature, type: 'DIGITAL_SIGNATURE' });
        }
        
        // Bank proof (required for all)
        if (formData.bankProof && !uploadedFiles['BANK_PROOF']) {
          filesToUpload.push({ file: formData.bankProof, type: 'BANK_PROOF' });
        }
        
        if (isIndia(formData.countryOrigin)) {
          // Indian vendors
          if (formData.panDocument && !uploadedFiles['PAN_CARD']) {
            filesToUpload.push({ file: formData.panDocument, type: 'PAN_CARD' });
          }
          if (formData.gstDocument && !uploadedFiles['GST_CERTIFICATE']) {
            filesToUpload.push({ file: formData.gstDocument, type: 'GST_CERTIFICATE' });
          }
          if (formData.msmeStatus === 'registered' && formData.msmeCertificate && !uploadedFiles['MSME_CERTIFICATE']) {
            filesToUpload.push({ file: formData.msmeCertificate, type: 'MSME_CERTIFICATE' });
          }
          if (formData.msmeStatus === 'not-registered' && formData.msmeDeclarationFile && !uploadedFiles['MSME_DECLARATION']) {
            filesToUpload.push({ file: formData.msmeDeclarationFile, type: 'MSME_DECLARATION' });
          }
        } else {
          // Foreign vendors
          if (formData.incorporationCertificate && !uploadedFiles['INCORPORATION_CERTIFICATE']) {
            filesToUpload.push({ file: formData.incorporationCertificate, type: 'INCORPORATION_CERTIFICATE' });
          }
        }
        
        // Upload files sequentially
        for (const { file, type } of filesToUpload) {
          console.log(`📤 Uploading ${type}...`);
          await uploadFile(file, type);
          console.log(`✅ ${type} uploaded`);
        }
        
        // Step 3: Generate agreement PDFs (if signed)
        console.log('📄 Generating agreement PDFs...');
        const generatedAgreements = [];
        
        try {
          // Import agreement generators
          const {
            generateNDABlob,
            generateSQABlob,
            generate4MDeclarationBlob,
            generateCodeOfConductBlob,
            generateComplianceAgreementBlob,
            generateSelfDeclarationBlob,
            generateOverseasAgreementBlob,
          } = await import('../../utils/documentGenerator.jsx');
          
          // Helper function to convert File to base64 data URL
          const fileToBase64 = (file) => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
          };
          
          // Convert digital signature to base64 if available
          let digitalSignatureDataUrl = null;
          if (formData.digitalSignature instanceof File) {
            try {
              digitalSignatureDataUrl = await fileToBase64(formData.digitalSignature);
              console.log('✅ Digital signature converted to base64 for agreements');
            } catch (error) {
              console.error('❌ Failed to convert digital signature:', error);
              // Continue without signature
            }
          } else {
            console.log('⚠️ No digital signature file found in formData');
          }
          
          // SANITIZE vendor data to prevent circular references and memory issues
          const sanitizeVendorData = (data) => {
            try {
              // Create a clean copy without circular references
              const cleanData = {
                companyName: String(data.companyName || ''),
                contactPersonName: String(data.contactPersonName || ''),
                designation: String(data.designation || ''),
                submissionDate: String(data.submissionDate || ''),
                registeredAddress: String(data.registeredAddress || ''),
                registeredCity: String(data.registeredCity || ''),
                registeredState: String(data.registeredState || ''),
                registeredCountry: String(data.registeredCountry || ''),
                registeredPincode: String(data.registeredPincode || ''),
                supplyAddress: String(data.supplyAddress || ''),
                supplyCity: String(data.supplyCity || ''),
                supplyState: String(data.supplyState || ''),
                supplyCountry: String(data.supplyCountry || ''),
                supplyPincode: String(data.supplyPincode || ''),
                countryOrigin: String(data.countryOrigin || ''),
                businessVertical: String(data.businessVertical || ''),
                businessDescription: String(data.businessDescription || ''),
                email: String(data.email || ''),
                phone: String(data.phone || ''),
              };
              
              // Handle digital signature safely - validate and limit size
              if (data.digitalSignature && typeof data.digitalSignature === 'string') {
                // Check if signature is too large (>15KB base64 ≈ 10KB image)
                const base64Size = data.digitalSignature.length;
                const estimatedFileSize = (base64Size * 3) / 4; // Approximate actual file size
                
                if (estimatedFileSize > 10 * 1024) {
                  console.warn('⚠️ Digital signature exceeds 10KB limit, this may cause issues', {
                    estimatedSize: Math.round(estimatedFileSize / 1024) + 'KB'
                  });
                }
                cleanData.digitalSignature = data.digitalSignature;
              }
              
              console.log('✅ Vendor data sanitized successfully', {
                hasSignature: !!cleanData.digitalSignature,
                signatureSize: cleanData.digitalSignature ? cleanData.digitalSignature.length : 0
              });
              
              return cleanData;
            } catch (error) {
              console.error('❌ Failed to sanitize vendor data:', error);
              // Return data without digitalSignature as fallback
              const { digitalSignature, ...safeData } = data;
              console.warn('⚠️ Returning vendor data without digital signature due to sanitization error');
              return safeData;
            }
          };
          
          // Prepare vendor data for agreement generation
          const rawVendorData = {
            companyName: formData.companyName,
            contactPersonName: formData.contactPersonName,
            designation: formData.designation,
            submissionDate: new Date().toISOString(),
            registeredAddress: formData.registeredAddress,
            registeredCity: formData.registeredCity,
            registeredState: formData.registeredState,
            registeredCountry: formData.registeredCountry,
            registeredPincode: formData.registeredPincode,
            supplyAddress: formData.supplyAddress,
            supplyCity: formData.supplyCity,
            supplyState: formData.supplyState,
            supplyCountry: formData.supplyCountry,
            supplyPincode: formData.supplyPincode,
            countryOrigin: formData.countryOrigin,
            businessVertical: formData.businessVertical,
            businessDescription: formData.businessDescription,
            email: formData.email,
            phone: formData.phoneNumber,
            digitalSignature: digitalSignatureDataUrl,
          };
          
          const vendorData = sanitizeVendorData(rawVendorData);
          
          // RETRY LOGIC: Generate with automatic retry on failure
          const generateWithRetry = async (generatorFn, vendorData, agreementName, maxRetries = 3) => {
            for (let attempt = 1; attempt <= maxRetries; attempt++) {
              try {
                console.log(`🔄 Attempt ${attempt}/${maxRetries}: Generating ${agreementName}...`);
                
                const pdfBlob = await generatorFn(vendorData);
                
                if (!pdfBlob || pdfBlob.size === 0) {
                  throw new Error('Generated blob is empty');
                }
                
                console.log(`✅ Successfully generated ${agreementName} on attempt ${attempt} (size: ${pdfBlob.size} bytes)`);
                return pdfBlob;
              } catch (error) {
                console.warn(`⚠️ Attempt ${attempt}/${maxRetries} failed for ${agreementName}:`, error.message);
                
                if (attempt === maxRetries) {
                  console.error(`❌ All ${maxRetries} attempts failed for ${agreementName}`);
                  throw error; // Final attempt failed
                }
                
                // Wait before retry (exponential backoff: 300ms, 600ms, 1200ms)
                const waitTime = 300 * Math.pow(2, attempt - 1);
                console.log(`⏳ Waiting ${waitTime}ms before retry...`);
                await new Promise(resolve => setTimeout(resolve, waitTime));
              }
            }
          };
          
          // Generate agreements based on what vendor signed
          const agreementGenerators = [
            { 
              name: 'NDA', 
              generator: generateNDABlob, 
              filename: 'NDA_Agreement.pdf',
              signed: formData.agreements.nda 
            },
            { 
              name: 'SQA', 
              generator: generateSQABlob, 
              filename: 'SQA_Agreement.pdf',
              signed: formData.agreements.sqa 
            },
            { 
              name: '4M Declaration', 
              generator: generate4MDeclarationBlob, 
              filename: '4M_Declaration.pdf',
              signed: formData.agreements.fourM 
            },
            { 
              name: 'Code of Conduct', 
              generator: generateCodeOfConductBlob, 
              filename: 'Code_of_Conduct.pdf',
              signed: formData.agreements.codeOfConduct 
            },
            { 
              name: 'Compliance Agreement', 
              generator: generateComplianceAgreementBlob, 
              filename: 'Compliance_Agreement.pdf',
              signed: formData.agreements.complianceAgreement 
            },
            { 
              name: 'Self Declaration', 
              generator: generateSelfDeclarationBlob, 
              filename: 'Self_Declaration.pdf',
              signed: formData.agreements.selfDeclaration 
            },
            { 
              name: 'Overseas Agreement', 
              generator: generateOverseasAgreementBlob, 
              filename: 'Overseas_Agreement.pdf',
              signed: formData.agreements.overseasAgreement 
            },
          ];
          
          // Track failed agreements for better error reporting
          const failedAgreements = [];
          
          for (const agreement of agreementGenerators) {
            if (agreement.signed) {
              try {
                console.log(`📄 AGREEMENT GENERATION: Starting generation for ${agreement.name}...`);
                
                // Use retry logic for robust generation
                const pdfBlob = await generateWithRetry(
                  agreement.generator,
                  vendorData,
                  agreement.name,
                  3 // max 3 retries
                );
                
                console.log(`📄 AGREEMENT GENERATION: ${agreement.name} blob created (size: ${pdfBlob.size} bytes, type: ${pdfBlob.type})`);
                
                // Convert to base64
                const arrayBuffer = await pdfBlob.arrayBuffer();
                const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
                
                console.log(`📄 AGREEMENT GENERATION: ${agreement.name} converted to base64 (original: ${pdfBlob.size} bytes, base64 length: ${base64.length} chars)`);
                
                generatedAgreements.push({
                  filename: agreement.filename,
                  content: base64,
                  contentType: 'application/pdf'
                });
                
                console.log(`✅ AGREEMENT GENERATION: ${agreement.name} successfully generated and added to array`, {
                  filename: agreement.filename,
                  blobSize: pdfBlob.size,
                  base64Length: base64.length,
                  contentType: 'application/pdf'
                });
              } catch (error) {
                console.error(`❌ AGREEMENT GENERATION: Failed to generate ${agreement.name} after all retries:`, error);
                console.error(`❌ AGREEMENT GENERATION: Error details:`, {
                  name: agreement.name,
                  filename: agreement.filename,
                  errorMessage: error.message,
                  errorStack: error.stack
                });
                
                // Track failed agreement
                failedAgreements.push({
                  name: agreement.name,
                  error: error.message
                });
              }
            } else {
              console.log(`⏭️ AGREEMENT GENERATION: Skipping ${agreement.name} (not signed)`);
            }
          }
          
          console.log(`✅ AGREEMENT GENERATION: Generated ${generatedAgreements.length} agreement PDF(s)`, {
            totalAgreements: generatedAgreements.length,
            filenames: generatedAgreements.map(a => a.filename),
            sizes: generatedAgreements.map(a => ({ filename: a.filename, base64Length: a.content.length }))
          });
          
          // CRITICAL VALIDATION: Ensure all signed agreements were generated
          const signedAgreementNames = agreementGenerators
            .filter(ag => ag.signed)
            .map(ag => ag.name);
          
          if (signedAgreementNames.length > 0 && generatedAgreements.length === 0) {
            const errorMsg = `Failed to generate any agreement PDFs (${signedAgreementNames.length} were signed). ${failedAgreements.length > 0 ? 'Errors: ' + failedAgreements.map(f => `${f.name}: ${f.error}`).join('; ') : ''}`;
            console.error('❌ VALIDATION FAILED:', errorMsg);
            throw new Error(
              `Unable to generate agreement documents. Please try again or contact support if the issue persists.`
            );
          }
          
          if (generatedAgreements.length < signedAgreementNames.length) {
            const generatedNames = generatedAgreements.map(a => a.filename.replace('_Agreement.pdf', '').replace('_Declaration.pdf', '').replace(/_/g, ' '));
            const missingNames = signedAgreementNames.filter(
              name => !generatedNames.some(gn => gn.toLowerCase().includes(name.toLowerCase()))
            );
            
            const errorMsg = `Failed to generate ${missingNames.length} of ${signedAgreementNames.length} agreements: ${missingNames.join(', ')}`;
            console.error('❌ VALIDATION FAILED:', errorMsg);
            
            throw new Error(
              `Failed to generate the following agreements: ${missingNames.join(', ')}. Registration cannot proceed without all required agreements. Please try again.`
            );
          }
          
          console.log('✅ VALIDATION PASSED: All signed agreements generated successfully', {
            expectedCount: signedAgreementNames.length,
            generatedCount: generatedAgreements.length,
            allAgreements: signedAgreementNames
          });
          
        } catch (error) {
          console.error('❌ Critical error in agreement generation:', error);
          // Don't continue - throw the error to block submission
          throw error;
        }
        
        // Step 4: Submit registration with generated agreements
        console.log('📤 REGISTRATION SUBMISSION: Preparing to submit registration...', {
          sessionId,
          generatedAgreementsCount: generatedAgreements.length,
          agreementFilenames: generatedAgreements.map(a => a.filename),
          agreementSizes: generatedAgreements.map(a => ({ filename: a.filename, base64Length: a.content.length }))
        });
        
        const submissionId = `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        console.log('📤 REGISTRATION SUBMISSION: Calling registrationService.submit with agreements...', {
          submissionId,
          sessionId,
          agreementCount: generatedAgreements.length
        });
        
        const result = await registrationService.submit(sessionId, submissionId, generatedAgreements);
        
        if (result.success) {
          console.log('✅ REGISTRATION SUBMISSION: Registration completed successfully!', {
            resultData: result.data,
            submittedAgreementCount: generatedAgreements.length,
            submittedAgreementFilenames: generatedAgreements.map(a => a.filename)
          });
          
          // Clear stored data
          localStorage.removeItem('vendorRegistrationForm');
          localStorage.removeItem('PICL_registration_session');
          
          setIsSubmitted(true);
        } else {
          throw new Error(result.error?.message || 'Registration submission failed');
        }
        
      } catch (error) {
        console.error('❌ Submission error:', error);
        
        const errorMessage = getErrorMessage(error);
        setErrors({ submit: errorMessage });
        
        // Scroll to top to show error
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CompanyInformationStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            onPreviewDocument={handleDocumentPreview}
          />
        );
      case 2:
        return (
          <AddressDetailsStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 3:
        return (
          <BankInformationStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            onPreviewDocument={handleDocumentPreview}
            keepExistingDocuments={keepExistingDocuments}
            setKeepExistingDocuments={setKeepExistingDocuments}
            uploadedFiles={uploadedFiles}
          />
        );
      case 4:
        return (
          <CategorizationStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            onPreviewDocument={handleDocumentPreview}
          />
        );
      case 5:
        return isIndia(formData.countryOrigin) ? (
          <ComplianceStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            onPreviewDocument={handleDocumentPreview}
            keepExistingDocuments={keepExistingDocuments}
            setKeepExistingDocuments={setKeepExistingDocuments}
            uploadedFiles={uploadedFiles}
          />
        ) : (
          <AgreementStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            onGeneratePDF={handleGeneratePDF}
            onPreviewDocument={handleDocumentPreview}
            keepExistingDocuments={keepExistingDocuments}
            setKeepExistingDocuments={setKeepExistingDocuments}
            uploadedFiles={uploadedFiles}
          />
        );
      case 6:
        return (
          <AgreementStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            onGeneratePDF={handleGeneratePDF}
            onPreviewDocument={handleDocumentPreview}
            keepExistingDocuments={keepExistingDocuments}
            setKeepExistingDocuments={setKeepExistingDocuments}
            uploadedFiles={uploadedFiles}
          />
        );
      default:
        return null;
    }
  };

  const canProceed = canProceedToNextStep(getEffectiveValidationStep(currentStep), formData, keepExistingDocuments);

  return (
    <div className="h-screen overflow-y-auto overflow-x-hidden bg-background">
      {/* Validation Error Toast Notification */}
      {showValidationError && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
          <div className="bg-red-600 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center space-x-3 max-w-md">
            <Icon name="AlertTriangle" size={24} className="flex-shrink-0" />
            <div>
              <p className="font-semibold">Validation Failed</p>
              <p className="text-sm text-red-100">
                {Object.keys(errors).length === 1 
                  ? '1 field needs correction' 
                  : `${Object.keys(errors).length} fields need correction`}
              </p>
            </div>
            <button 
              onClick={() => setShowValidationError(false)}
              className="ml-4 text-white hover:text-red-200"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>
      )}
      
      {isSubmitted && (
        <div className="max-w-2xl mx-auto px-6 py-16 min-h-screen flex items-center justify-center">
          <div className="bg-white border border-border rounded-lg shadow-lg p-8 text-center w-full">
            <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={32} color="white" />
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-2">
              {regularizationMode ? 'Updates Submitted Successfully' : 'Request sent successfully'}
            </h3>
            <p className="text-text-secondary mb-6">
              {regularizationMode
                ? 'Your vendor information updates have been submitted for admin review. The administrator will review your changes and update your vendor profile accordingly.'
                : 'Your vendor registration request has been submitted. Our team will review your application and contact you.'}
            </p>
            <div className="bg-muted/30 p-4 rounded-lg mb-6 text-left">
              <div className="text-sm text-text-secondary">
                <div className="flex justify-between mb-2">
                  <span>Reference:</span>
                  <span className="font-mono font-medium">VR-{Date.now().toString().slice(-8)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Submitted:</span>
                  <span>{new Date().toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            <Button variant="default" onClick={() => window.close()}>Close</Button>
          </div>
        </div>
      )}
      {!isSubmitted && (
        <FormHeader currentStep={currentStep} totalSteps={totalSteps} regularizationMode={regularizationMode} />
      )}
      
      {/* Session Status Banner */}
      {!isSubmitted && !inviteError && !regularizationError && sessionId && (
        <div className="max-w-4xl mx-auto px-6 mt-4">
          <SessionBanner 
            timeRemaining={timeRemaining} 
            totalSteps={totalSteps}
            savedSteps={new Set(Object.keys(uploadedFiles))}
            lastAutoSave={lastAutoSave}
            isAutoSaving={isAutoSaving}
          />
        </div>
      )}
      
      {/* Loading state while session initializes */}
      {sessionLoading && !inviteError && !regularizationError && (
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">
            {regularizationMode ? 'Loading regularization form...' : 'Initializing registration session...'}
          </p>
        </div>
      )}

      {/* Block UI if invite missing/invalid or session failed to initialize */}
      {(inviteError || regularizationError || (!sessionLoading && !sessionId && !isSubmitted && !regularizationMode)) && (
        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="bg-error/10 border border-error/20 rounded-lg p-6 text-center">
            <h3 className="text-xl font-semibold text-error mb-2">
              {regularizationError
                ? 'Regularization Error'
                : inviteError 
                  ? (inviteError.includes('expired') 
                      ? 'Invite Expired' 
                      : inviteError.includes('Invalid') || inviteError.includes('not found')
                      ? 'Invalid Invite Link'
                      : inviteError.includes('Network')
                      ? 'Connection Error'
                      : 'Registration Error')
                  : 'Session initialization failed'}
            </h3>
            <p className="text-text-secondary mb-4">
              {regularizationError || inviteError || 'Unable to start registration session. Please check your invite link and try again.'}
            </p>
            <div className="flex gap-3 justify-center">
              {inviteError && (
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Retry
                </button>
              )}
              {!inviteError && (
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Form Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 pb-32">
        {/* Error Summary - Show at top */}
        {!isSubmitted && Object.keys(errors).length > 0 && (
          <ErrorSummary errors={errors} currentStep={currentStep} />
        )}
        
        {!isSubmitted && !inviteError && !regularizationError && !sessionLoading && sessionId && renderCurrentStep()}
        
        {/* Error Summary - Also show at bottom for reference */}
        {!isSubmitted && Object.keys(errors).length > 0 && (
          <div className="mt-8 bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
              <div>
                <h4 className="font-medium text-error mb-2">Please fix the following errors:</h4>
                <ul className="text-sm text-error space-y-1">
                  {Object.entries(errors).map(([field, error]) => (
                    <li key={field}>• {typeof error === 'string' ? error : 'Please check this field'}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form Navigation */}
      {!isSubmitted && !inviteError && !regularizationError && !sessionLoading && sessionId && (
        <FormNavigation
          currentStep={currentStep}
          totalSteps={totalSteps}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          canProceed={canProceed}
        />
      )}
    </div>
  );
};

export default PublicVendorRegistrationForm;